import React, { useCallback, useMemo, useState } from 'react';
import {
  PROMO_CODES_PAGE_SIZE,
  PROMO_CODE_STATUSES,
  PROMO_CODE_STATUS_LABELS,
  filterPromoCodes,
  paginatePromoCodes,
  usePromoCodes,
} from '../../../domain/Campaigns';
import useClampedPage from '../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../commonComponents/DsPagination';
import { SelectFilter } from '../../commonComponents/filters/SelectFilter';
import { ListPage } from '../../commonComponents/ListPage';
import { PromoCodesTable } from './components/PromoCodesTable';

const ALL = '';

const STATUS_OPTIONS = [
  { value: ALL, label: 'All' },
  ...PROMO_CODE_STATUSES.map((value) => ({ value, label: PROMO_CODE_STATUS_LABELS[value] })),
];

/**
 * Programs → Promo Codes (GOTU-5661): every discount campaign platform-wide,
 * newest first, with its date-derived status. View-only for V2.
 *
 * Search, the status filter and paging are client-side over one fetch of the
 * whole set — see domain/Campaigns/promoCodes.js for why. The page holds the
 * three filter values and nothing else.
 */
export const PromoCodes = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(ALL);

  const {
    promoCodes, isPending, isFetching, isError,
  } = usePromoCodes();

  const filtered = useMemo(
    () => filterPromoCodes(promoCodes, { search, status }),
    [promoCodes, search, status],
  );
  const rows = useMemo(() => paginatePromoCodes(filtered, page), [filtered, page]);

  // Narrowing a filter can leave the page past the end of the shorter list;
  // pull it back into range so the table never dead-ends on an empty slice.
  // A failed fetch has no total (nullish → never clamp).
  useClampedPage(page, isError ? null : filtered.length, PROMO_CODES_PAGE_SIZE, setPage);

  // Re-slicing the list makes the current page number meaningless: page 3 of
  // the unfiltered list is not page 3 of the filtered one.
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value) => {
    setStatus(value);
    setPage(1);
  }, []);

  const hasFilters = search !== '' || status !== ALL;

  return (
    <ListPage
      title="Promo Codes"
      isLoading={isFetching && promoCodes.length > 0}
      total={isError ? undefined : filtered.length}
      filters={(
        <>
          <DebouncedSearchField
            label="Search"
            placeholder="Search by name or code"
            value={search}
            onChange={handleSearchChange}
          />

          <SelectFilter
            label="Code Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={handleStatusChange}
          />
        </>
      )}
    >
      <PromoCodesTable
        promoCodes={rows}
        isPending={isPending}
        isError={isError}
        hasFilters={hasFilters}
        footer={(
          <DsPagination
            page={page}
            pageSize={PROMO_CODES_PAGE_SIZE}
            totalCount={filtered.length}
            onPageChange={setPage}
          />
        )}
      />
    </ListPage>
  );
};

export default PromoCodes;
